import { createClient } from '@supabase/supabase-js'

// Supabase configuration for Vite
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://dxcreyobefaylwyahbty.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR4Y3JleW9iZWZheWx3eWFoYnR5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU4MDk1OTIsImV4cCI6MjA3MTM4NTU5Mn0.fBKoSYe4lUlntSD35NHPs6fCTEiJ29YpsRy5KXsseCE'

// Validate configuration
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase configuration. Please check your .env file.')
}

console.log('🚀 Supabase configured:', { 
  url: supabaseUrl,
  keyPrefix: supabaseAnonKey.slice(0, 20) + '...'
})

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Export types for TypeScript
export type Database = {
  public: {
    Tables: {
      // User profiles extending Supabase auth.users
      profiles: {
        Row: {
          id: string
          email: string
          full_name: string | null
          avatar_url: string | null
          credits_balance: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          email: string
          full_name?: string | null
          avatar_url?: string | null
          credits_balance?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          full_name?: string | null
          avatar_url?: string | null
          credits_balance?: number
          created_at?: string
          updated_at?: string
        }
      }
      // Raffles table
      raffles: {
        Row: {
          id: string
          title: string
          description: string
          creator_id: string
          type: 'numbers' | 'names' | 'animals' | 'custom'
          status: 'draft' | 'active' | 'completed' | 'cancelled'
          total_spots: number
          spot_price: number
          prize_description: string
          draw_date: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          description: string
          creator_id: string
          type: 'numbers' | 'names' | 'animals' | 'custom'
          status?: 'draft' | 'active' | 'completed' | 'cancelled'
          total_spots: number
          spot_price: number
          prize_description: string
          draw_date: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          description?: string
          creator_id?: string
          type?: 'numbers' | 'names' | 'animals' | 'custom'
          status?: 'draft' | 'active' | 'completed' | 'cancelled'
          total_spots?: number
          spot_price?: number
          prize_description?: string
          draw_date?: string
          created_at?: string
          updated_at?: string
        }
      }
      // Raffle participations/reservations
      raffle_participations: {
        Row: {
          id: string
          raffle_id: string
          user_id: string
          spot_numbers: number[]
          payment_status: 'pending' | 'paid' | 'cancelled'
          payment_proof_url: string | null
          reserved_at: string
          paid_at: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          raffle_id: string
          user_id: string
          spot_numbers: number[]
          payment_status?: 'pending' | 'paid' | 'cancelled'
          payment_proof_url?: string | null
          reserved_at?: string
          paid_at?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          raffle_id?: string
          user_id?: string
          spot_numbers?: number[]
          payment_status?: 'pending' | 'paid' | 'cancelled'
          payment_proof_url?: string | null
          reserved_at?: string
          paid_at?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      // Credit purchases
      credit_purchases: {
        Row: {
          id: string
          user_id: string
          amount: number
          credits_purchased: number
          payment_method: 'pix' | 'card' | 'transfer'
          payment_status: 'pending' | 'approved' | 'rejected'
          payment_proof_url: string | null
          pix_instructions: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          amount: number
          credits_purchased: number
          payment_method: 'pix' | 'card' | 'transfer'
          payment_status?: 'pending' | 'approved' | 'rejected'
          payment_proof_url?: string | null
          pix_instructions?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          amount?: number
          credits_purchased?: number
          payment_method?: 'pix' | 'card' | 'transfer'
          payment_status?: 'pending' | 'approved' | 'rejected'
          payment_proof_url?: string | null
          pix_instructions?: string | null
          created_at?: string
          updated_at?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
  }
}